/**
 * ReceiptDrilldownModal — click any Status pill in the Post-Order Table
 * to open a modal showing the exact receipt's full scan chain and any
 * attached photo evidence. Pulls units linked to this MCI + receipt
 * location from `/mtx/material-units`, then their scan events from
 * `/mtx/receipts/<receiptNumber>/scan-chain`.
 */
import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { X, Package, ScanLine, Camera, Loader2, MapPin, Truck } from 'lucide-react';

const C = { primary: '#1E4E8C', primaryText: 'var(--tone-navy-text)', accent: '#F26B1F', slate: 'var(--text-primary)',
             slateMuted: 'var(--text-muted)', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444', info: '#3B82F6' };

const SCAN_TYPE_ICON = {
  SUPPLIER_QR: Package, WH_INBOUND: Truck, IQC_DESK: ScanLine,
  BIN_PUTAWAY: MapPin, TRANSFER: MapPin, WH_OUTBOUND: Truck,
  SITE_INBOUND: MapPin, DELIVERY: Truck,
};

export default function ReceiptDrilldownModal({ projectId, mciLineNumber, poNumber, poLineItem,
                                                  receipt, remaining, unit, onClose }) {
  const [data, setData] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const isOpenBalance = !receipt;   // no receipt → open-balance pill was clicked

  useEffect(() => {
    if (isOpenBalance) return;   // no scan chain to fetch for open-balance
    (async () => {
      try {
        const { data: d } = await api.get(
          `/v1/projects/${projectId}/mci/entries/${encodeURIComponent(mciLineNumber)}/receipts/${encodeURIComponent(receipt.receiptNumber)}/scan-chain`);
        setData(d);
      } catch { setData({ error: 'Failed to load scan chain', events: [], units: [] }); }
    })();
  }, [projectId, mciLineNumber, receipt?.receiptNumber, isOpenBalance]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.55)' }} onClick={onClose}>
      <div className="w-[720px] max-h-[85vh] bg-[var(--card-bg)] rounded-lg shadow-2xl overflow-y-auto"
        onClick={e => e.stopPropagation()} data-testid="receipt-drilldown-modal">
        {/* Header */}
        <div className="sticky top-0 z-10 px-4 py-3 flex items-center justify-between border-b"
          style={{ borderColor: C.border,
                    background: isOpenBalance ? 'linear-gradient(90deg, #FEF3C7, #FDE68A)' : 'linear-gradient(90deg, #D1FAE5, #A7F3D0)' }}>
          <div>
            <div className="flex items-center gap-2">
              <span className="px-1.5 py-0.5 rounded font-bold uppercase tracking-wider text-white text-[10px]"
                style={{ background: isOpenBalance ? C.warn : C.success }}>
                {isOpenBalance ? 'Open Balance' : 'Accepted Receipt'}
              </span>
              <span className="font-mono font-bold text-sm" style={{ color: C.slate }}>{poNumber} · L{poLineItem}</span>
            </div>
            <div className="text-[10px] mt-0.5" style={{ color: C.slateMuted }}>
              MCI <b>{mciLineNumber}</b>
              {receipt && <> · Receipt <b className="font-mono">{receipt.receiptNumber}</b> · Received <b>{receipt.receiptDate?.slice(0, 10)}</b> · <b>{receipt.acceptedQty} {receipt.unit || unit}</b> stored at <b>{receipt.acceptedLocation}</b></>}
              {isOpenBalance && <> · <b>{remaining} {unit}</b> still due — no scan events yet</>}
            </div>
          </div>
          <button onClick={onClose} data-testid="receipt-drilldown-close">
            <X className="w-5 h-5" style={{ color: C.slateMuted }} />
          </button>
        </div>

        <div className="p-4">
          {isOpenBalance && (
            <div className="p-6 text-center text-[11px] italic rounded-lg" style={{ color: C.slateMuted, background: 'var(--tone-amber-bg)' }}>
              This bucket represents the remaining <b>{remaining} {unit}</b> of the PO that hasn't landed yet.<br />
              Scan-chain evidence becomes available once each receipt physically arrives on site.
            </div>
          )}
          {!isOpenBalance && !data && (
            <div className="p-6 text-center text-sm" style={{ color: C.slateMuted }}>
              <Loader2 className="w-4 h-4 mx-auto animate-spin mb-2" /> Loading scan chain…
            </div>
          )}
          {data && data.error && (
            <div className="p-4 text-center text-[11px] italic rounded-lg"
              style={{ color: C.danger, background: 'var(--tone-red-bg)' }}>{data.error}</div>
          )}
          {data && !data.error && (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="p-2 rounded border" style={{ borderColor: C.border }}>
                  <div className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Units on this receipt</div>
                  <div className="text-lg font-bold font-mono" style={{ color: C.primaryText }}>{data.units?.length || 0}</div>
                </div>
                <div className="p-2 rounded border" style={{ borderColor: C.border }}>
                  <div className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Scan events</div>
                  <div className="text-lg font-bold font-mono" style={{ color: C.accent }}>{data.events?.length || 0}</div>
                </div>
                <div className="p-2 rounded border" style={{ borderColor: C.border }}>
                  <div className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Photos</div>
                  <div className="text-lg font-bold font-mono" style={{ color: C.info }}>
                    {(data.events || []).reduce((s, e) => s + (e.photoUrls?.length || 0), 0)}
                  </div>
                </div>
              </div>

              {/* Scan chain timeline */}
              <div className="mb-3">
                <div className="text-[10px] uppercase font-semibold mb-2" style={{ color: C.primaryText }}>Scan Chain Timeline</div>
                {(data.events || []).length === 0 && (
                  <div className="p-4 text-center text-[10px] italic rounded" style={{ color: C.slateMuted, background: 'var(--card-bg-alt)' }}>
                    No scan events recorded against this receipt yet.
                  </div>
                )}
                <div className="space-y-1.5">
                  {(data.events || []).map((e, i) => {
                    const Icon = SCAN_TYPE_ICON[e.interfacePointType] || ScanLine;
                    return (
                      <div key={i} className="flex gap-2 p-2 rounded border" style={{ borderColor: C.border, background: 'var(--card-bg-alt)' }}
                        data-testid={`scan-event-${i}`}>
                        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ background: 'var(--card-bg-alt)' }}>
                          <Icon className="w-4 h-4" style={{ color: C.primaryText }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10.5px] font-bold" style={{ color: C.slate }}>{e.interfacePointType?.replace(/_/g,' ')}</span>
                            {e.previousState !== e.newState && (
                              <span className="text-[9px]" style={{ color: C.slateMuted }}>
                                {e.previousState} → <b style={{ color: C.primaryText }}>{e.newState}</b>
                              </span>
                            )}
                            {e.previousLocation && e.newLocation && e.previousLocation !== e.newLocation && (
                              <span className="text-[9px]" style={{ color: C.slateMuted }}>
                                {e.previousLocation} → <b style={{ color: C.accent }}>{e.newLocation}</b>
                              </span>
                            )}
                            <span className="ml-auto text-[9px] font-mono" style={{ color: C.slateMuted }}>
                              {new Date(e.scannedAt).toLocaleString()}
                            </span>
                          </div>
                          <div className="text-[9.5px] mt-0.5" style={{ color: C.slateMuted }}>
                            Unit <b className="font-mono">{e.materialUnitId?.slice(-8)}</b>
                            {e.scannedBy && <> · by <b>{e.scannedBy}</b></>}
                            {e.hashCurrent && <> · <span className="font-mono text-[8.5px]">chain #{e.hashCurrent.slice(0, 6)}</span></>}
                          </div>
                          {e.notes && <div className="text-[9.5px] italic mt-0.5" style={{ color: C.slate }}>"{e.notes}"</div>}
                          {(e.photoUrls || []).length > 0 && (
                            <div className="flex gap-1 mt-1.5" data-testid={`scan-event-photos-${i}`}>
                              {e.photoUrls.map((url, pi) => (
                                <button key={pi} onClick={() => setSelectedPhoto(url)}
                                  className="w-12 h-12 rounded overflow-hidden border hover:opacity-90"
                                  style={{ borderColor: C.border }}
                                  data-testid={`scan-photo-${i}-${pi}`}>
                                  <img src={url} alt="" className="w-full h-full object-cover" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Units on this receipt */}
              {(data.units || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.primaryText }}>Units on this Receipt · {data.units.length}</div>
                  <div className="rounded border overflow-x-auto" style={{ borderColor: C.border, maxHeight: 200 }}>
                    <table className="w-full text-[10px]">
                      <thead style={{ background: 'var(--card-bg-alt)' }}>
                        <tr>
                          <th className="text-left p-1 uppercase font-semibold text-[8.5px]" style={{ color: C.slateMuted }}>Unit ID</th>
                          <th className="text-left p-1 uppercase font-semibold text-[8.5px]" style={{ color: C.slateMuted }}>State</th>
                          <th className="text-left p-1 uppercase font-semibold text-[8.5px]" style={{ color: C.slateMuted }}>Bin</th>
                          <th className="text-right p-1 uppercase font-semibold text-[8.5px]" style={{ color: C.slateMuted }}>Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.units.map(u => (
                          <tr key={u.id} style={{ borderTop: `1px solid ${C.border}` }}>
                            <td className="p-1 font-mono font-bold" style={{ color: C.primaryText }}>{u.id}</td>
                            <td className="p-1" style={{ color: C.slate }}>{u.currentState}</td>
                            <td className="p-1 text-[9px]" style={{ color: C.slateMuted }}>{u.currentBin || '—'}</td>
                            <td className="p-1 text-right font-mono">{u.quantity ?? 1}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {selectedPhoto && (
          <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-8"
            onClick={() => setSelectedPhoto(null)}>
            <img src={selectedPhoto} alt="Scan evidence" className="max-w-full max-h-full rounded shadow-2xl" />
          </div>
        )}
      </div>
    </div>
  );
}
